/**
 * MasterDataLicenseFormDialog — Create/Edit `license`.
 * 1:1 port of Access `frmMdQryLicenseAdd`. Compact Sales Contract style.
 *
 * Field order (Access): License cert no → CP Code (combo cp_vn) → CP Name (ro)
 * → Valid from → Valid to → SI → SD → Valid type (Valid/Invalid) → Note.
 * VBA rule: is_valid = (valid_type === 'Valid') ? 0 : 1.
 */
import {
  Autocomplete,
  createFilterOptions,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useLookupVendorsQuery } from '@/store/api/lookupsApi'
import {
  useCreateMdLicenseMutation,
  useUpdateMdLicenseMutation,
} from '@/store/api/masterDataCrudApi'
import type { LookupVendor, MasterDataLicense } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleSx, numSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataLicense | null
  onClose: () => void
  onSaved?: () => void
}

type ValidType = 'Valid' | 'Invalid'
const VALID_OPTIONS: ValidType[] = ['Valid', 'Invalid']

// cp_code combo (Access ColumnCount=2 code+name): dropdown "code — name", ô input
// SAU khi chọn chỉ hiện code (bound column). Filter match cả code lẫn name.
const vendorFilter = createFilterOptions<LookupVendor>({
  stringify: (o) => `${o.code} ${o.name ?? ''}`,
})

// SI/SD: ô number thuần (chị Đẹp chốt) → strip/parse sang number|null khi save.
const parseNum = (s: string): number | null => {
  const t = s.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

type FormState = {
  license_cert_no: string
  cp_code: string
  cp_name: string
  valid_from: string
  valid_to: string
  si: string
  sd: string
  valid_type: ValidType | ''
  note: string
}

const EMPTY: FormState = {
  license_cert_no: '',
  cp_code: '',
  cp_name: '',
  valid_from: '',
  valid_to: '',
  si: '',
  sd: '',
  valid_type: '',
  note: '',
}

export default function MasterDataLicenseFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createLicense, { isLoading: creating }] = useCreateMdLicenseMutation()
  const [updateLicense, { isLoading: updating }] = useUpdateMdLicenseMutation()
  const loading = creating || updating

  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' })

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        license_cert_no: initialValue.license_cert_no ?? '',
        cp_code: initialValue.cp_code ?? '',
        cp_name: initialValue.name ?? '',
        valid_from: initialValue.valid_from ?? '',
        valid_to: initialValue.valid_to ?? '',
        si: initialValue.si != null ? String(initialValue.si) : '',
        sd: initialValue.sd != null ? String(initialValue.sd) : '',
        valid_type: initialValue.is_valid === 0 ? 'Valid' : 'Invalid',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // Vendor combo: inject selected vào options nếu chưa có (tắt "no match").
  const selectedVendor = useMemo<LookupVendor | null>(() => {
    if (!form.cp_code) return null
    return (
      vendors.find((v) => v.code === form.cp_code) ??
      ({ code: form.cp_code, name: form.cp_name, short_name: null, vat_code: null } as LookupVendor)
    )
  }, [vendors, form.cp_code, form.cp_name])
  const vendorOpts = useMemo(
    () =>
      selectedVendor && !vendors.some((o) => o.code === selectedVendor.code)
        ? [selectedVendor, ...vendors]
        : vendors,
    [vendors, selectedVendor],
  )

  const certEmpty = !form.license_cert_no.trim()
  const cpEmpty = !form.cp_code.trim()
  const fromEmpty = !form.valid_from
  const toEmpty = !form.valid_to
  const typeEmpty = !form.valid_type
  // Valid To phải >= Valid From (chỉ check khi cả hai đã nhập).
  const dateRangeInvalid =
    !!form.valid_from && !!form.valid_to && dayjs(form.valid_to).isBefore(dayjs(form.valid_from), 'day')
  const canSave =
    !certEmpty && !cpEmpty && !fromEmpty && !toEmpty && !typeEmpty && !dateRangeInvalid && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (certEmpty || cpEmpty || fromEmpty || toEmpty || typeEmpty || dateRangeInvalid) {
      setShowErrors(true)
      return
    }
    const body = {
      license_cert_no: form.license_cert_no.trim(),
      cp_code: form.cp_code.trim(),
      valid_from: form.valid_from,
      valid_to: form.valid_to,
      is_valid: form.valid_type === 'Valid' ? 0 : 1,
      si: parseNum(form.si),
      sd: parseNum(form.sd),
      note: form.note.trim() || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateLicense({ id: initialValue.license_id, body }).unwrap()
        snack.success('License updated')
      } else {
        await createLicense(body).unwrap()
        snack.success('License created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit License' : 'New License'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.license_cert_no}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="License cert no"
              size="small"
              fullWidth
              required
              error={showErrors && certEmpty}
              value={form.license_cert_no}
              onChange={(e) => set('license_cert_no', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              options={vendorOpts}
              value={selectedVendor}
              onChange={(_e, v) => {
                set('cp_code', v?.code ?? '')
                set('cp_name', v?.name ?? '')
              }}
              getOptionLabel={(opt) => opt?.code ?? ''}
              filterOptions={vendorFilter}
              renderOption={(props, opt) => (
                <li {...props} key={opt.code}>{opt.name ? `${opt.code} — ${opt.name}` : opt.code}</li>
              )}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="CP Code"
                  required
                  error={showErrors && cpEmpty}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 2 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="CP Name"
              size="small"
              fullWidth
              value={form.cp_name}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label="Valid from"
              format="DD-MMM-YYYY"
              value={form.valid_from ? dayjs(form.valid_from) : null}
              onChange={(d) => set('valid_from', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && fromEmpty,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 3 },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label="Valid to"
              format="DD-MMM-YYYY"
              value={form.valid_to ? dayjs(form.valid_to) : null}
              minDate={form.valid_from ? dayjs(form.valid_from) : undefined}
              onChange={(d) => set('valid_to', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && (toEmpty || dateRangeInvalid),
                  helperText: dateRangeInvalid ? 'Valid To must be on or after Valid From.' : undefined,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 4 },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="SI (VNĐ/KG)"
              size="small"
              fullWidth
              type="number"
              value={form.si}
              onChange={(e) => set('si', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="SD (VNĐ/KG)"
              size="small"
              fullWidth
              type="number"
              value={form.sd}
              onChange={(e) => set('sd', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 6 }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              options={VALID_OPTIONS}
              value={form.valid_type || null}
              onChange={(_e, v) => set('valid_type', (v ?? '') as ValidType | '')}
              getOptionLabel={(o) => o ?? ''}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Valid type"
                  required
                  error={showErrors && typeEmpty}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 7 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 8 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
